// -----------------------------------------------------------------------------
// Anaglyphic, Netherlands @ 2023.
// See README in the root project for more information.
// -----------------------------------------------------------------------------

import { BaseStore, useBaseStore } from "@/stores/base";
import type { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import { useRouter } from "vue-router";
import { appInit } from "@/app-init";
import i18n from "@/plugins/i18n";
import { AUTH_MODULES } from "@/ui/Enums";

/** Various route guards. */
export namespace RouteGuards {
	/** Route guard parameters. */
	export interface Params {
		to?: RouteLocationNormalized;
		from?: RouteLocationNormalized;
		next?: NavigationGuardNext;
		store?: BaseStore;
	}

	/**
	 * Check if the user has the required permissions to access the route.
	 * @param meta The route meta.
	 * @param store The base store.
	 *
	 * @returns True if the user has the required permissions, false otherwise.
	 */
	export const hasPermissions = ({ to, store }: Params): boolean => {
		let userHasRequiredPermissions = true;

		if (to.meta.permissions) {
			to.meta.permissions.forEach((permission: string) => {
				if (!store.HAS_PERMISSION(permission)) {
					userHasRequiredPermissions = false;
				}
			});
		}
		return userHasRequiredPermissions;
	};

	export const getRouteByPath = (
		path: string
	): RouteLocationNormalized | null => {
		const router = useRouter();
		return router.resolve({ path }) ?? null;
	};

	/**
	 * Check if the user is authenticated.
	 */
	export const isUserAuthenticated = ({ to, store }: Params): boolean => {
		if (to.matched.some((record) => record.meta.requiresAuth)) {
			if (!store.isAuthenticated) {
				return false;
			}
		}
		return true;
	};

	export const isTwoFactorAuthenticationRequiredAndNotSetByUser = ({
		to,
		store,
	}: Params): boolean => {
		if (to.matched.some((record) => record.meta.requiresAuth)) {
			if (
				store?.currentEnvironment.is_two_factor_authentication_required &&
				store?.currentUser.authentication_module ===
					AUTH_MODULES.MOTIVES_NATIVE &&
				!store?.currentUser.has2FA
			) {
				return true;
			}
		}
		return false;
	};

	export const dataInit = async ({ to, store }: Params): Promise<boolean> => {
		if (
			(to.meta.requiresAuth || typeof to.meta.requiresAuth === "undefined") &&
			!store.currentUser
		) {
			try {
				const response = await appInit();
				i18n.global.locale.value =
					useBaseStore()?.currentUser?.derivedLanguage?.locale || "en";
				if (response.isOk()) {
					return true;
				} else {
					return false;
				}
			} catch (error) {
				return false;
			}
		} else {
			return true;
		}
	};

	/**
	 * Check if the user is already authenticated if the login route is requested.
	 */
	export const isUserAlreadyAuthed = ({ to, store }: Params): boolean => {
		if (to.name === "login" && store.isAuthenticated) {
			return true;
		}
		return false;
	};
}
